import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24Bin24, IconS24Plus24 } from '@is24/cosma-ui-icons'
import { Indicator } from 'is24-corecss'
import { AttachmentDataWithRequiredAttachment } from '../../../../../../../../../../@types/types'
import { convertImageUrl } from '../../../../../../../../../../services/conversion/conversionServices'
import styles from './WebViewPictureThumbnailStrip.module.css'

type WebViewPictureThumbnailStripProps = {
  attachments: AttachmentDataWithRequiredAttachment[]
  selectedPictureId: string
  onSelect: (pictureId: string) => void
  onDelete?: (attachment: AttachmentDataWithRequiredAttachment) => void
  onAddClick: () => void
}

export const WebViewPictureThumbnailStrip: React.FC<WebViewPictureThumbnailStripProps> = ({
  attachments,
  selectedPictureId,
  onSelect,
  onDelete,
  onAddClick,
}) => {
  const { t } = useTranslation()
  const selectedThumbnail = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    selectedThumbnail.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [selectedPictureId])

  return (
    <div className={styles.thumbnailStrip} data-testid='web-view-picture-thumbnail-strip'>
      <button
        aria-label={t('wizard.screen.multimedia.webview.inlinePictures.addImage')}
        className={styles.addTile}
        type='button'
        onClick={onAddClick}
      >
        <IconS24Plus24 aria-hidden />
      </button>
      {attachments.map((attachment, index) => {
        const id = attachment.attachment.id
        const isSelected = selectedPictureId === id
        return (
          <div className={styles.thumbnailTile} key={id}>
            <button
              type='button'
              ref={isSelected ? selectedThumbnail : undefined}
              className={styles.selectButton}
              aria-label={t('wizard.screen.multimedia.webview.inlinePictures.selectImage', {
                position: index + 1,
              })}
              aria-pressed={isSelected}
              onClick={() => onSelect(id)}
            >
              <img
                alt={attachment.attachment.title || ''}
                className={styles.thumbnail}
                src={convertImageUrl('160', '160', attachment.attachment.url)}
              />
              {index === 0 && (
                <Indicator
                  asSpan
                  appearance='label'
                  labelColor='white'
                  labelVariant='flat'
                  labelSize='small'
                  className={styles.titlePicture}
                >
                  {t('wizard.screen.multimedia.cover')}
                </Indicator>
              )}
            </button>
            {onDelete && (
              <button
                aria-label={t('wizard.screen.multimedia.webview.inlinePictures.deleteImage', {
                  position: index + 1,
                })}
                className={styles.deleteButton}
                onClick={() => onDelete(attachment)}
                type='button'
              >
                <IconS24Bin24 aria-hidden />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
